import { getProgramGfaAreas } from './design/program-gfa.helpers';
import {
  getProjectMapPreview,
  type PolygonUnion,
} from './design/building-map-preview.helpers';
import {
  ElementKind,
  Baseline,
  OneOfElements,
  OneOfParentElements,
  Project,
  IBuilding,
  ProjectMetadata,
  IProjectInfo,
  OneOfChildElements,
  IElement,
  OneOfListElements,
  OneOfElementListElements,
  OneOfElementListChildren,
  TrimmedProject,
  CreatedElement,
  defaultBuildingFeatures,
  IBuildingFeatures,
  DEFAULT_BASELINE_NAME,
} from '../models/project.model';
import {
  applyChanges,
  getId,
  getKeys,
  isPartialEqual,
  omit,
  omitUndefined,
  pick,
  replaceProperties,
} from './object.helpers';
import {
  getAllBuildingVersions,
  getBuilding,
  getChildElements,
  getElementById,
  isBuildingVersionElement,
  isProductElement,
  flattenElements,
  isElement,
  getPathToElement,
  OneOfSearchableElements,
  isOneOfChildElements,
  findElementAndParent,
  getBuildingVersionById,
} from './recursive-element.helpers';
import {
  findDuplicates,
  getNeighborPreferringPrevious,
  hasDuplicates,
  includesSome,
  shallowEqualMap,
} from './array.helpers';
import {
  DeepPartial,
  ItemOrItemId,
  Merge,
  SemiPartial,
} from '../models/type-helpers.model';
import { IElementProperty } from '../models/element-property.model';
import {
  createElementFromPartial,
  createElementOfType,
  getElementIdMap,
  IElementVersionMap,
} from './element-factory.helpers';
import { produce } from 'immer';
import { ensureVersionHasProposal } from './proposal-factory.helpers';
import { IProduct, ProductRecord } from '../models/product.model';
import {
  applyElementCategory,
  getElementCategory,
  isElementCategoryID,
  applyElementCategoryProperties,
} from './element-category.helpers';
import { DateTime } from 'luxon';
import { autoUpdateElementQuantity } from './auto-quantity.helpers';
import {
  getDefaultProductCountExpressionFromParent,
  getElementQuantityByName,
} from './element-quantity.helpers';
import { ElementQuantityName } from '../models/element-quantities.model';
import {
  getProductIdsInElement,
  getProductsInElement,
} from './product.helpers';
import {
  ExpressionVariables,
  ExpressionVariablesRecord,
  applyProductVariables,
  defaultVariables,
  formatExpressionVariableName,
} from './expression-variables.helpers';
import { EXPRESSION_VARIABLE_NAME } from '../constants/expression-variables-names.constant';
import { ElementPropertyName } from '../models/element-property.model';
import { isEqual } from 'lodash';
import { findFreeName } from './string.helpers';
import { mergeDuplicatedResultsIntoRecord } from './proposal-results-record.helpers';
import { sortInsert } from './sort.helpers';
import { getElementCategoryId } from './element.helpers';
import { requiredKind } from './element-kind.helpers';
import {
  getProposalsInVersion,
  updateElementActiveVersionStates,
} from './proposal.helpers';
import { RecipeRecord } from '../models/recipe.model';
import { getRecipeName } from './recipe.helpers';
import { applyRegenerateIdsOptions, RegenerateIds } from './id.helpers';
import { required } from './function.helpers';
import { slimProjectResults } from './sbef-results.helpers';
import { getBuildingLifetimeFromMetadata } from './design/legacy-metadata-expression-fields.helpers';
import type { LegacyProjectMetadataForExpression } from './design/legacy-metadata-expression-fields.helpers';
import {
  IFactoryVersion,
  OneOfChildFactoryElements,
  OneOfFactoryElements,
} from '../models/factory-element.model';
import {
  isInheritableFallbackCount,
  getElementAndQuantityProperties,
  PropertyChange,
} from './element-property.helpers';
import { ProjectChangeType } from './pipeline/update-engine.models';
import { elementPropertyFallbackCountToChange } from './pipeline/updates/element-property-fallback-count.update';
import { elementQuantityFallbackCountToChange } from './pipeline/updates/element-quantity-fallback-count.update';
import { mergeElementPartials } from './pipeline/updates/merge-element-partials.helpers';
import {
  createExpression,
  isExpressionValue,
} from './expression-factory.helpers';
import { toNumber } from './math.helpers';
import { syncDataTableProperties } from './data-tables';
import { IDataTable } from '@shared/models/data-table.model';
import { guards } from './typeguards.helpers';
import { IInsertSortPlacement } from '@shared/models/sort.model';
import { getDesignGfa, getGfaFromStoreys } from './design/shape.helpers';
import { designGuards } from './design/design-typeguards.helpers';
import { ensureDesignHasBuilding } from './design/factories/design.factory';

export type IAddElementOptions = {
  isSibling?: boolean;
  navigate?: boolean;
  regenerateIds?: RegenerateIds;
  skipEnrichment?: boolean;
  skipServerUpdate?: boolean;
  generateUniqueName?: boolean;
  siblingId?: string;
  placement?: IInsertSortPlacement;
};

/**
 * Get a version by id and throw error if it not exist.
 * If you wanna check if a version exist or not use Array.some instead.
 * TODO: REMOVE, use getBuildingVersionById instead
 * @param project
 * @param id
 * @param throwIfNotFound Defaults to false
 */
export const getVersionById = <
  T extends true | false | undefined = true,
  R = T extends true ? Baseline : Baseline | undefined,
>(
  project: Project | IBuilding | Baseline,
  id: string,
  throwIfNotFound: T = true as T,
): R => {
  const version = getAllBuildingVersions(project).find((v) => v.id === id);

  if (!version && throwIfNotFound) {
    throw new Error('could not find version');
  }
  return version as R;
};

export const getProjectMeta = (project: Project | IBuilding): ProjectMetadata =>
  getBuilding(project).meta;

export const getBuildingFeatures = (building: IBuilding): IBuildingFeatures =>
  building.features ?? defaultBuildingFeatures;

export const getBuildingLifetime = (project: Project | IBuilding): number =>
  getBuildingLifetimeFromMetadata(getProjectMeta(project));

/**
 * Update properties in building. Currently there is only one building
 * Note that the properties of the building you want to update is enough to pass.
 */
export const updateProjectProperties = (
  project: Project,
  projectChanges: Partial<Project>,
): Project => {
  return produce(project, (draft) => {
    Object.assign(draft, projectChanges);
  });
};

/**
 * Merge {@link changes} onto the project (flattened building fields live on
 * {@link Project} after DEV-2576). Pass only the fields to update.
 * @param project Project to update
 * @param changes Partial project/building fields to merge
 * @returns Updated project, or the original project if nothing changed
 */
export const updateBuilding = (
  project: Project,
  changes: Partial<IBuilding>,
  ignoreUndefined = true,
): Project => {
  const building = getBuilding(project);
  const nextChanges = changes.design
    ? { ...changes, design: ensureDesignHasBuilding(changes.design) }
    : changes;

  // Make changes to the building while making sure we don't accidentally remove any properties (forbidden for building)
  const updatedBuilding = replaceProperties(
    building,
    nextChanges,
    ignoreUndefined,
  );

  // Return original project if no changes were made
  return updatedBuilding !== building
    ? {
        ...project,
        ...updatedBuilding,
      }
    : project;
};

export const updateProjectMetadata = (
  project: Project,
  metaUpdates: Partial<LegacyProjectMetadataForExpression>,
  features?: Partial<IBuildingFeatures>,
): Project => {
  const meta = getProjectMeta(project);
  const prevFeatures = getBuildingFeatures(getBuilding(project));
  replaceProperties(prevFeatures, features ?? {});
  return updateBuilding(project, {
    meta: applyChanges(meta, metaUpdates),
    features: replaceProperties(prevFeatures, features),
  });
};

/**
 * Update properties in a specific version (any building).
 * Note that the properties of the version you want to update is enough to pass.
 * TODO: Is this really needed? Can't updateElements be used instead?
 */
export const updateBuildingVersionProperties = (
  project: Project,
  ...versionChanges: Array<SemiPartial<Baseline, 'id'>>
): Project => {
  if (versionChanges.length === 0) return project;

  for (const changes of versionChanges) {
    getVersionById(project, changes.id);
  }

  const applyPatchesToBuildingVersions = (versions: Baseline[]): Baseline[] => {
    return shallowEqualMap(versions, (version) => {
      let next: Baseline = version;
      for (const changes of versionChanges) {
        if (changes.id !== version.id) continue;
        // Match prior immer behavior: compare each patch to the original version, not accumulated `next`
        if (!hasChanges(version, changes)) continue;
        next = { ...next, ...changes };
      }
      return next;
    });
  };

  const newBaselines = applyPatchesToBuildingVersions(project.baselines);
  if (newBaselines === project.baselines) return project;

  return {
    ...project,
    baselines: newBaselines,
  };
};

/**
 * Replaces the building version with matching {@link Baseline.id} by {@link nextVersion}.
 *
 * Unlike {@link updateBuildingVersionProperties}, this swaps the whole node so keys missing from
 * {@link nextVersion} (e.g. rollout-only `proposalsV2`) are not retained from the previous object.
 *
 * @param project - Project that contains {@link nextVersion.id} on some building
 * @param nextVersion - Full version snapshot to persist
 */
export const replaceBuildingVersionInProject = (
  project: Project,
  nextVersion: Baseline,
): Project => {
  const newBaselines = shallowEqualMap(project.baselines, (v) =>
    v.id === nextVersion.id ? nextVersion : v,
  );
  if (newBaselines === project.baselines) return project;
  return { ...project, baselines: newBaselines };
};

/**
 * Update a product in a version.
 * Add product if it doesn't exist.
 * @param project
 * @param version
 * @param updatedProduct
 * @returns
 */
export const updateProductInVersion = (
  project: Project,
  version: Baseline,
  updatedProduct: IProduct,
): Project => {
  const id = updatedProduct.id;
  const { products: versionProducts } = version;

  // Don't update if the product is the same
  if (isEqual(updatedProduct, versionProducts[id])) {
    return project;
  }

  const updatedProjectProducts: Record<string, IProduct> = {
    ...versionProducts,
    [id]: omitUndefined(updatedProduct) as IProduct,
  };

  const updatedVersion: Baseline = {
    ...version,
    products: updatedProjectProducts,
  };

  return updateBuildingVersionProperties(project, updatedVersion);
};

export const removeVersionFromProject = (
  project: Project,
  versionId: string,
): Project => {
  return produce(project, (draft) => {
    const building = getBuilding(draft);
    building.baselines = building.baselines.filter((v) => v.id !== versionId);
    return draft;
  });
};

/**
 * Returns the automatic name for the next version. Starting from the resulting
 * version count, it advances to the first free numeric name.
 */
export const getNextVersionName = (
  versions: readonly Pick<Baseline, 'name'>[],
): string =>
  findFreeName([...versions], `Version ${versions.length + 1}`, {
    firstAvailableIndex: true,
  });

/**
 * Returns the first free duplicate name derived from the source version.
 * `Anbud` becomes `Anbud 2`; occupied suffixes are advanced without gaps.
 */
export const getDuplicateVersionName = (
  versions: readonly Pick<Baseline, 'name'>[],
  sourceName: string,
): string =>
  findFreeName([...versions], sourceName, { firstAvailableIndex: true });

/** A version factory whose name is assigned by the add helpers, not the caller. */
type VersionAddBase = Omit<Partial<IFactoryVersion>, 'name'>;

/**
 * Add new versions to project.
 * Can create a new or create a copy of an existing version.
 * `name` on the factory is ignored; the next free version name is assigned.
 * @param project
 * @param baseVersion Optional partials of versions to base the new versions on
 * @returns
 */
export const addVersionToProject = (
  project: Project,
  regenerateIds: RegenerateIds,
  ...baseVersions: VersionAddBase[]
): Project =>
  addVersionsToProject(project, regenerateIds, undefined, baseVersions);

/** Add a duplicate while deriving its name from the source version. */
export const addDuplicateVersionToProject = (
  project: Project,
  regenerateIds: RegenerateIds,
  duplicateNameBase: string,
  baseVersion: VersionAddBase,
): Project =>
  addVersionsToProject(project, regenerateIds, duplicateNameBase, [
    baseVersion,
  ]);

const addVersionsToProject = (
  project: Project,
  regenerateIds: RegenerateIds,
  duplicateNameBase: string | undefined,
  initialBaseVersions: VersionAddBase[],
): Project => {
  // Make sure to always have at least one base version
  const baseVersions =
    initialBaseVersions.length === 0 ? [{}] : initialBaseVersions;

  return produce(project, (draft) => {
    const building = getBuilding(draft);

    baseVersions.forEach((baseVersion) => {
      const name = duplicateNameBase
        ? getDuplicateVersionName(building.baselines, duplicateNameBase)
        : getNextVersionName(building.baselines);
      // The import file belongs to the source version. Elements already in the
      // tree are copied; the file, its discards and its drafts are not.
      const { imports: _imports, ...versionWithoutImportFile } = baseVersion;
      const version = ensureVersionHasProposal(
        createElementOfType(
          ElementKind.Version,
          {
            ...versionWithoutImportFile,
            name,
            // Always rotate proposal ids when adding a version: legacy data + duplicate paths
            // could otherwise leak the same proposal id onto multiple versions and break
            // {@link findProposalLocation} routing (DEV-3313).
            proposals: stripProposalIdsForFreshGeneration(
              baseVersion.proposals,
            ),
          },
          // Match child {@link addElements} roots so pre-generated ids from {@link useAddElement} survive.
          applyRegenerateIdsOptions(regenerateIds, { isRoot: true }),
        ),
      );
      building.baselines = [...building.baselines, version];
    });
  });
};

/**
 * Drop `id` from each proposal so {@link createProposal} hands out a fresh uuid even when the
 * caller passed `regenerateIds: false` (e.g. moves that preserve every other id). Proposal ids
 * are not stable across versions and re-using them across versions breaks routing (DEV-3313).
 */
const stripProposalIdsForFreshGeneration = (
  proposals: Partial<IFactoryVersion>['proposals'] | undefined,
): Partial<IFactoryVersion>['proposals'] | undefined => {
  if (!proposals?.length) return proposals;
  return proposals.map(({ id: _id, ...rest }) => rest);
};

/**
 * Add factory children to {@link parent}. Child elements/products go into a building version;
 * {@link IFactoryVersion} bases on a {@link Project} root go through {@link addVersionToProject}.
 *
 * TODO: rewrite addElements to support adding elements in any parent
 * @param root Project or version owning `parent`
 * @param parent Element or version that receives the new children
 * @returns The updated root and the created elements
 */
export const addElements = <
  R extends Baseline | Project,
  T extends (R extends Baseline
    ? OneOfChildFactoryElements
    : OneOfFactoryElements),
  N extends CreatedElement<T['kind']>,
>(
  root: R,
  parent: OneOfParentElements,
  {
    regenerateIds = true,
    generateUniqueName = true,
    placement = 'first',
    siblingId,
  }: IAddElementOptions,
  ...elementBases: T[]
): {
  root: R;
  addedElements: CreatedElement<T['kind']>[];
} => {
  const versionBases: IFactoryVersion[] = [];
  const childElementBases: OneOfChildFactoryElements[] = [];
  for (const element of elementBases) {
    if (element.kind === ElementKind.Version) {
      versionBases.push(element);
      continue;
    }
    childElementBases.push(element);
  }
  const added: N[] = [];

  // Version to modify
  const version = isBuildingVersionElement(parent)
    ? parent
    : getPathToElement(root, parent)[0];

  if (!isBuildingVersionElement(version)) {
    throw new Error('No building version found for parent element');
  }

  // Add any building versions first (Project root only).
  if (versionBases.length > 0) {
    if (isBuildingVersionElement(root)) {
      throw new Error('Can not add buildVersion to buildingVersion');
    }
    const existingVersionIds = getAllBuildingVersions(root).map(getId);
    root = addVersionToProject(root, regenerateIds, ...versionBases) as R;
    getAllBuildingVersions(root).forEach((v) => {
      if (!existingVersionIds.includes(v.id)) {
        added.push(v as N);
      }
    });
  }

  if (childElementBases.length === 0) {
    return { root, addedElements: added };
  }

  let updatedRoot = produce(root, (draft) => {
    const draftVersion = getVersionById(draft, version.id);
    const draftParent: OneOfParentElements = getElementById(
      draftVersion,
      parent.id,
    );

    // Create an array of new elements to add from the partials
    const newElements: OneOfChildElements[] = childElementBases.map(
      (partialElement) => {
        const categoryID = getElementCategoryId(partialElement);

        const { name, fallbackName } = generateElementNameAndFallbackName(
          parent,
          partialElement,
          generateUniqueName,
        );

        // Mark this call as the root of the factory subtree so callers can opt into
        // {@link IRegenerateIdsOptions.preserveRootElementIds} (e.g. when an outer flow
        // pre-generated the new element id and wants children regenerated).
        let element = createElementFromPartial(
          { ...partialElement, name, fallbackName },
          applyRegenerateIdsOptions(regenerateIds, { isRoot: true }),
        );

        // Apply category and seed all properties etc if category is provided
        if (isElement(element) && isElementCategoryID(categoryID)) {
          return applyElementCategory(element, categoryID);
        }

        // Manual product add: inherit parent quantity like category-generated slots (DEV-3365)
        const hasExplicitProductCount =
          partialElement.kind === ElementKind.Product &&
          partialElement.count !== undefined;
        if (
          isProductElement(element) &&
          isElement(draftParent) &&
          !hasExplicitProductCount
        ) {
          element = {
            ...element,
            count: createExpression(
              getDefaultProductCountExpressionFromParent(draftParent),
            ),
          };
        }

        return element;
      },
    );

    const draftSibling: OneOfChildElements | undefined = siblingId
      ? requiredKind(
          getElementById(draftVersion, siblingId),
          ElementKind.Element,
          ElementKind.Product,
        )
      : undefined;

    if (!draftParent) {
      throw new Error('Could not find parent');
    }
    if (isProductElement(draftParent)) {
      throw new Error(`Can't add elements to products`);
    }

    draftParent.elements = sortInsert(
      placement,
      getChildElements(draftParent),
      draftSibling,
      ...newElements,
    );

    added.push(...(newElements as N[]));
  });

  // Map between previous and new ids
  const idMap = getElementIdMap(
    childElementBases,
    (added as OneOfElements[]).filter(isOneOfChildElements),
    regenerateIds,
  );

  // Remap proposal result records on duplicated elements
  updatedRoot = applyProposalResultsOnDuplicatedElements(
    updatedRoot,
    version.id,
    idMap,
  );

  const updatedVersion = getVersionById(updatedRoot, version.id);

  const addedIds = added.map(getId);

  // Get latest instances of added elements
  const addedElements = flattenElements(updatedVersion).filter((e) =>
    addedIds.includes(e.id),
  );

  if (addedElements.length !== added.length) {
    throw new Error('Not all added elements are valid');
  }

  return {
    root: updatedRoot,
    addedElements: addedElements as CreatedElement<T['kind']>[],
  };
};

/**
 * Make sure proposal results are remapped when duplicating elements.
 * @param root Version or project to apply the the changes to
 * @param versionId Version to apply the changes to
 * @param idMap Mapping between old and new ids
 * @returns
 */
const applyProposalResultsOnDuplicatedElements = <R extends Baseline | Project>(
  root: R,
  versionId: Baseline['id'],
  idMap: IElementVersionMap | undefined,
): R => {
  if (!idMap) {
    return root;
  }
  const version = getVersionById(root, versionId);
  const proposals = getProposalsInVersion(version).map((p) => ({
    ...p,
    resultsRecord: mergeDuplicatedResultsIntoRecord(
      p.resultsRecord,
      idMap.elementMap,
    ),
  }));

  return updateElements<Baseline, R>(
    root,
    updateElementActiveVersionStates({
      ...version,
      proposals,
    }),
  );
};

/**
 * Replace a building version with a new version (if changed)
 * @param project
 * @param version
 * @returns
 */
export const replaceBuildingVersion = (
  project: Project,
  version: Baseline,
): Project => {
  const prevVersion = getVersionById(project, version.id, false);

  if (!prevVersion || prevVersion === version) {
    return project;
  }

  return produce(project, (draft) => {
    const building = getBuilding(draft);
    building.baselines = building.baselines.map((v) =>
      v.id === version.id ? version : v,
    );
  });
};

/**
 * Update elements with one or more changes.
 * Note. Passing undefined properties will update the property with an undefined value.
 * @param project
 * @param elementChanges
 * @returns
 */
export const updateElements = <
  T extends OneOfElements,
  R extends OneOfSearchableElements,
>(
  root: R,
  ...elementChanges: (SemiPartial<T, 'id'> | T | undefined)[]
): R => {
  const changes = elementChanges.filter(guards.is.defined);

  // Throw error if duplicate updates since they might overwrite each other
  if (hasDuplicates(changes.map((e) => e.id))) {
    const duplicateIds = findDuplicates(changes.map((e) => e.id));
    const rootContext =
      'baselines' in root
        ? `project.id=${(root as Project).id} project.name="${(root as Project).name}"`
        : (root as Baseline).kind === ElementKind.Version
          ? `version.id=${(root as Baseline).id} version.name="${(root as Baseline).name}"`
          : `root.id=${'id' in root ? root.id : 'unknown'}`;
    const duplicateChanges = changes.filter((c) => duplicateIds.includes(c.id));
    console.error('[updateElements] Duplicate element updates:', {
      duplicateIds,
      duplicateChanges,
      rootContext,
      changeCount: changes.length,
      changeIds: changes.map((c) => c.id),
    });
    throw new Error(
      `Duplicate element updates: ids [${duplicateIds.join(', ')}] in ${rootContext}`,
    );
  }
  // Return root unmodified if no changes
  if (!changes.length) {
    return root;
  }

  return produce(root, (draft) => {
    changes.filter(guards.is.defined).forEach((changes) => {
      const element = getElementById(root, changes.id);
      const draftElement = getElementById(draft, changes.id);

      if (!element || !draftElement) {
        throw new Error('Could not find element to update');
      }
      // Only update if needed to not trigger to many rerenders of views
      if (hasChanges(element, changes as Partial<T>)) {
        Object.assign(draftElement, changes);
      }
    });
  });
};

export const removeBuildingVersion = (
  project: Project,
  versionOrId: ItemOrItemId<Baseline>,
): Project => {
  const id = getId(versionOrId);
  return produce(project, (draft) => {
    const building = getBuilding(draft);

    if (draft.active_version_id === id) {
      const neighbor = getNeighborPreferringPrevious(building.baselines, id);
      if (neighbor) {
        draft.active_version_id = neighbor.id;
      }
    }
    getVersionById(building, id, true); // Crash if version is not found
    building.baselines = building.baselines.filter((v) => v.id !== id);
  });
};

/**
 * Remove child elements from a project or version.
 *
 * Building versions are not child elements — remove those with
 * {@link removeBuildingVersion} (or {@link ProjectChangeType.VersionRemove} through the
 * update engine).
 *
 * @param root Project or Version to remove elements from
 * @param elementOrIds Elements or ids to remove
 */
export const removeElements = <
  R extends Baseline | Project,
  T extends (R extends Baseline ? OneOfChildElements : OneOfElements),
>(
  root: R,
  ...elementOrIds: ItemOrItemId<T>[]
): R => {
  const elementIds = elementOrIds.map(getId);

  for (const id of elementIds) {
    if (getVersionById(root, id, false)) {
      throw new Error(`Use removeBuildingVersion to remove version ${id}`);
    }
  }

  return produce(root, (draft) => {
    elementIds.forEach((id) => {
      const { parent, element, path } = findElementAndParent(draft, id);

      // Check presence before the root kind: a miss leaves `path` empty, so asserting
      // path[0] first reported a kind mismatch instead of the actual problem.
      if (!parent || !element) {
        throw new Error(`Could not find element to remove: ${id}`);
      }
      requiredKind(path[0], ElementKind.Version);

      parent.elements = parent.elements.filter((e) => e.id !== id);
    });
  });
};

export const generateElementNameAndFallbackName = (
  parent: OneOfParentElements,
  elementBase: OneOfChildFactoryElements,
  generateUniqueName = false,
): { name?: string; fallbackName: string } => {
  const name =
    isElement(elementBase) && elementBase.name
      ? getNewNameOfElement(parent, elementBase, generateUniqueName)
      : undefined;

  const fallbackName = generateElementFallbackName(
    parent,
    elementBase,
    undefined,
    generateUniqueName,
  );

  return { name, fallbackName };
};

/**
 * Get a new name for the element and increases the number if it already exists
 * @param parent
 * @param elementBase
 * @returns
 */
export const getNewNameOfElement = (
  parent: OneOfParentElements,
  elementBase: Pick<Merge<OneOfListElements>, 'name' | 'kind'>,
  generateUniqueName = false,
): string | undefined => {
  // Products doesn't have names
  if (elementBase.kind === ElementKind.Product) {
    return undefined;
  }

  const defaultName =
    elementBase.kind === ElementKind.Version
      ? DEFAULT_BASELINE_NAME
      : 'Element 1';

  const currentName = elementBase.name;

  if (currentName && !generateUniqueName) {
    return currentName;
  }

  return getAvailableName(parent, currentName ?? defaultName);
};

/**
 * Get all names amd fallbackNmaes from the version
 * @param version
 * @returns
 */
const getNamesAndFallbackNames = (root: OneOfParentElements) => {
  const flattenedElements = flattenElements(root);

  const names = flattenedElements
    .map((el) => ('name' in el ? el.name : undefined))
    .filter(guards.is.defined);

  const fallbackNames = flattenedElements
    .map((el) => ('fallbackName' in el ? el.fallbackName : undefined))
    .filter(guards.is.defined);

  const set = new Set([...names, ...fallbackNames]);

  return [...set];
};

export const getFallbackName = (
  element: OneOfElements | OneOfFactoryElements,
): string | undefined =>
  'fallbackName' in element ? element.fallbackName : undefined;

/**
 * Generate an available fallback name for an element
 * @param root
 * @param element
 * @param recipeLookup
 * @returns
 */
export const generateElementFallbackName = (
  root: OneOfParentElements,
  element?: OneOfElements | OneOfFactoryElements,
  recipeLookup?: RecipeRecord,
  generateUniqueName = false,
): string => {
  if (!element) {
    return getAvailableName(root) ?? '';
  }

  const currentFallback = getFallbackName(element);
  const recipeName = getRecipeName(element, recipeLookup);

  const categoryId = getElementCategoryId(element);
  const categoryName = categoryId
    ? (getElementCategory(categoryId)?.name ?? 'None')
    : undefined;

  const baseName = recipeName ?? categoryName ?? 'Element';

  // Keep current if it has not been changed (if duplicating we wanna force regeneration)
  if (currentFallback?.startsWith(baseName) && !generateUniqueName) {
    return currentFallback;
  }

  return getAvailableName(root, baseName) ?? '';
};

/**
 * Get a name with an increased number if it already exists
 * @param root Root element to get names from
 * @param baseName Base name, will increment from there if name already exists
 * @returns
 */
export const getAvailableName = (
  root: OneOfParentElements,
  baseName = 'Element 1',
): string | undefined => {
  return findFreeName(getNamesAndFallbackNames(root), baseName);
};

/**
 * Test if the element has any changes
 * @param element
 * @param changes
 * @param keys If keys are provided, only those keys will be checked for changes
 * @returns
 */
export const hasChanges = <T extends OneOfElements>(
  element: T,
  changes: Partial<T>,
  ...keys: (keyof T)[]
): boolean =>
  element !== changes && // Don't check properties if the element is the same as the changes
  !isPartialEqual(element, changes, ...keys);

type ElementKey = keyof Merge<OneOfElementListElements>;

const IGNORED_MODIFIED_KEYS_LIST = ['elements'] satisfies ElementKey[];

type IgnoredKey = (typeof IGNORED_MODIFIED_KEYS_LIST)[number];

/** Includes `results` so legacy {@link IElement.results} does not mask canonical {@link Baseline.resultsRecord} diffs. */
const IGNORED_MODIFIED_KEYS = new Set<IgnoredKey | NonCollapsibleString>([
  ...IGNORED_MODIFIED_KEYS_LIST,
  'results',
]);

export const getModifiedProductIds = (
  next: Baseline,
  prev: Baseline | undefined,
): IProduct['id'][] => {
  const newProducts = next.products;
  const oldProducts = prev?.products ?? {};

  const modifiedProducts = Object.values(newProducts).filter((newProduct) => {
    const oldProduct = oldProducts[newProduct.id];
    return !isEqual(newProduct, oldProduct);
  });

  return modifiedProducts.map((p) => p.id);
};

/**
 * Get all changed elements between two version (or project) trees.
 * Includes elements removed in `nextRoot` (they only exist on `prevRoot`) so callers can rerun pipelines and clear stale results.
 * Ignores spurious `element.children` noise when comparing surviving nodes.
 * @param nextProject
 * @param prevProject
 * @param onlyRootElements If true (default), provide the top element of the changes, not the changed children as well
 * @returns
 */
export const getModifiedElements = <T extends Project | Baseline>(
  nextRoot: T,
  prevRoot?: T,
  onlyRootElements = true,
): OneOfElements[] => {
  // if equal we don't need to run this function
  if (nextRoot === prevRoot) {
    return [];
  }

  // If this is a project run this function on all versions
  if (!isBuildingVersionElement(nextRoot)) {
    const nextVersions = getAllBuildingVersions(nextRoot);
    const prevVersions = getAllBuildingVersions(prevRoot);
    return nextVersions.flatMap((v) =>
      getModifiedElements(
        v,
        prevVersions.find((p) => p.id === v.id),
        onlyRootElements,
      ),
    );
  }

  const prevVersion = prevRoot
    ? requiredKind(prevRoot, ElementKind.Version)
    : undefined;

  const nextElements = flattenElements(...nextRoot.elements);
  const prevElements = flattenElements(...(prevVersion?.elements ?? []));
  const modifiedProductIds = getModifiedProductIds(nextRoot, prevVersion);

  const changedElements = nextElements.filter((next) => {
    const prev = prevElements.find((p) => p.id === next.id);
    if (!prev) {
      return true;
    }
    // We want to detect if an EPD is added or changed to be able to recalculate density etc
    const isProductOrProductParent =
      getChildElements(next).some(isProductElement) || isProductElement(next);
    const productIds = isProductOrProductParent
      ? getProductIdsInElement(next)
      : [];

    // If referenced product have been changed (like cost or co2e) we consider the element to have changed
    if (includesSome(modifiedProductIds, ...productIds)) {
      return true;
    }
    if (prev === next) {
      return false;
    }

    // Ignore children when checking for changes since they always change when a child changes
    // Use keys from BOTH next and prev to detect added/removed properties (e.g. productOverrides).
    // Cannot use hasChanges/isPartialEqual here — they filter keys by `in changes`,
    // which misses properties that exist only on next (additions).
    const keys = getKeys({ ...next, ...prev }).filter(
      (k) => !IGNORED_MODIFIED_KEYS.has(k),
    );

    return !isEqual(pick(next, ...keys), pick(prev, ...keys));
  });

  const nextIds = new Set(nextElements.map(getId));
  // Removals never appear in nextElements; without this, deleting the last element yields [] and the enrich pipeline skips (stale proposal CO2/cost).
  const withRemovals = [...changedElements];
  for (const prevEl of prevElements) {
    if (
      !nextIds.has(prevEl.id) &&
      !withRemovals.some((e) => e.id === prevEl.id)
    ) {
      withRemovals.push(prevEl);
    }
  }

  // If onlyRootElements is true, only the top element of the changes, not the changed children as well
  if (onlyRootElements) {
    const children = withRemovals.flatMap((e) => getChildElements(e));
    return withRemovals.filter((e) => !children.includes(e));
  }

  return withRemovals;
};

export const isProjectsEqual = (a: Project, b: Project): boolean =>
  isEqual(omit(a, 'updated_at', 'enriched'), omit(b, 'updated_at', 'enriched'));

export const sortProjectsByLastUpdated = (
  projects: IProjectInfo[],
): IProjectInfo[] => {
  return [...projects].sort(
    (a, b) =>
      DateTime.fromISO(b.updated_at).valueOf() -
      DateTime.fromISO(a.updated_at).valueOf(),
  );
};

/**
 * Helper to get GFA from any kind of Project-like object
 */
export const getProjectGfa = (
  project: Project | TrimmedProject | IProjectInfo,
): number => {
  // Normal project => GFA from the building design (source of truth post-migration)
  if ('design' in project) {
    return getDesignGfa(project.design);
  }
  if ('metadata' in project) {
    // Prefer denormalized metadata GFA; fall back to legacy program_design if present.
    const design = project.program_design;
    if (designGuards.is.buildingDesign(design)) return getDesignGfa(design);
    if (design && 'storeys' in design && guards.is.array(design.storeys)) {
      return getGfaFromStoreys(design.storeys.filter(designGuards.is.storey));
    }
    return project.metadata?.design?.gfa ?? 0;
  }
  // Project info has gfa (older, can probably be removed)
  if ('gfa' in project && guards.is.number(project.gfa)) {
    return project.gfa;
  }
  return 0;
};

export const projectToProjectInfo = (
  project: Project | TrimmedProject,
  unionPolygons?: PolygonUnion,
): IProjectInfo => {
  const gfa = getProjectGfa(project); // IMPORTANT: Get GFA BEFORE removing heavy fields or metadata
  // Full GET/socket projects: design is authoritative. Trimmed list rows use
  // denormalized metadata (and optional legacy program_design during rollout).
  const design = 'design' in project ? project.design : project.program_design;
  const listMeta = 'metadata' in project ? project.metadata : undefined;
  const mutual: Omit<IProjectInfo, 'versionIds' | 'gfa'> = {
    ...omit(
      project,
      'baselines',
      'design',
      'meta',
      'features',
      'version_ids',
      'metadata',
      'program_design',
    ),
    id: String(project.id),
    kind: ElementKind.ProjectInfo,
    parent_id: project.parent_id === undefined ? null : project.parent_id,
    location: project.location ?? 0, // Not to be confused with Building design location which is a coordinate
  };

  // A full project carries baselines (source of truth); trimmed projects only
  // keep the DB-optimized version_ids copy, used here as fallback.
  const versionIds =
    'baselines' in project
      ? getAllBuildingVersions(project).map(getId)
      : project.version_ids;

  const activeVersion =
    'baselines' in project
      ? getBuildingVersionById(project, project.active_version_id)
      : undefined;
  const baselineResults =
    'baselines' in project
      ? activeVersion?.resultsRecord?.[project.active_version_id]
      : (project.baseline_results ?? listMeta?.baseline_results);
  const overviewResults =
    'baselines' in project
      ? (getProposalsInVersion(activeVersion)[0]?.resultsRecord?.[
          project.active_version_id
        ] ?? baselineResults)
      : (project.overview_results ?? listMeta?.overview_results);

  const building_count = design
    ? 'buildings' in design
      ? design.buildings.length
      : 1
    : (listMeta?.building_count ?? 0);

  return {
    ...mutual,
    results: overviewResults ?? baselineResults ?? project.results,
    versionIds,
    building_count,
    map_preview: design
      ? unionPolygons
        ? getProjectMapPreview(design, unionPolygons)
        : listMeta?.map_preview
      : listMeta?.map_preview,
    program_areas: design
      ? getProgramGfaAreas(design, gfa)
      : (listMeta?.program_areas ?? []),
    overview_results: overviewResults,
    baseline_results: baselineResults,
    gfa,
  };
};
/**
 * Will apply category properties and quantity properties to an element
 * and update children accordingly
 * @param element
 * @param path
 * @returns
 */
export const enrichElementStructure = <T extends OneOfElements>(
  element: T,
  path: OneOfParentElements[] = [],
  variablesRecord: ExpressionVariablesRecord = {},
  productRecord: ProductRecord = {},
  dataTables: IDataTable[] = [],
): T => {
  if (isElement(element)) {
    const parentFromPath = path.length > 0 ? path[path.length - 1] : undefined;
    const parentElement =
      parentFromPath && isElement(parentFromPath) ? parentFromPath : undefined;
    let updatedElement: IElement = updateInheritedPropertyFallbacks(
      element,
      variablesRecord?.[element.id] ?? defaultVariables,
      parentElement,
    );

    // If data tables are connected to the element, sync the properties
    updatedElement = syncDataTableProperties(updatedElement, dataTables);

    updatedElement = applyElementCategoryProperties(updatedElement);

    // // Products might have changed in the above call
    variablesRecord = updateProductIdsInVariablesRecord(
      updatedElement,
      variablesRecord,
      productRecord,
    );

    // Apply auto or clear quantities if possible
    updatedElement = autoUpdateElementQuantity(
      updatedElement,
      path,
      variablesRecord,
    );

    // Update element if it has changes
    if (updatedElement !== element) {
      element = updatedElement as T;
    }
  }

  // Recursively update children
  if (isElement(element) || isBuildingVersionElement(element)) {
    const updatedChildren: OneOfElementListChildren[] = element.elements.map(
      (child) =>
        enrichElementStructure(
          child,
          [...path, element as OneOfParentElements],
          variablesRecord,
          productRecord,
          dataTables,
        ),
    );
    const childIds = updatedChildren.map((e) => e.id);
    if (hasDuplicates(childIds)) {
      console.error(
        '[enrichElementStructure] Duplicate children after recursion:',
        {
          parentId: element.id,
          parentName: element.name,
          duplicateIds: findDuplicates(childIds),
          childIds,
        },
      );
    }
    // Update element if children has changed
    element = replaceProperties(element, {
      elements: updatedChildren,
    } as Partial<typeof element>);
  }

  return element;
};

const updateProductIdsInVariablesRecord = (
  element: OneOfElements,
  variablesRecord: ExpressionVariablesRecord,
  productRecord: ProductRecord = {},
): ExpressionVariablesRecord => {
  if (getKeys(productRecord).length === 0) {
    return variablesRecord;
  }
  const vars = variablesRecord[element.id];

  if (vars) {
    // Get all products in the element (IN ORDER!!!)
    const products = getProductsInElement(element, productRecord);
    const updatedVars = applyProductVariables(vars, products);
    if (updatedVars !== vars) {
      return {
        ...variablesRecord,
        [element.id]: updatedVars,
      };
    }
  }
  return variablesRecord;
};

/**
 * Populate {@link Project.active_version_id} and {@link Project.results} from the active version.
 * {@link Project.results} is the slim list/header rollup ({@link slimProjectResults}); the full
 * per-SBEF-group breakdown stays on {@link Baseline.resultsRecord}.
 * @param project
 * @param newActiveVersionId Provide to select a new active version, otherwise will use project.active_version_id or first version id if not provided
 */
export const applyVersionMetadata = (
  project: Project,
  newActiveVersionId?: Baseline['id'],
): Project => {
  const active_version_id = newActiveVersionId ?? project.active_version_id;
  const firstVersion = required(getAllBuildingVersions(project)[0]);

  const version =
    getBuildingVersionById(project, active_version_id) ?? firstVersion;

  return replaceProperties(project, {
    active_version_id: version.id,
    results: slimProjectResults(version.resultsRecord?.[version.id]),
  });
};

/**
 * Read a parent-scope variable for inherit fallback sync.
 * Lifetime inherits from project `building_lifetime` when `lifetime` is absent on parent.
 */
const resolveParentVariableForInheritFallback = (
  parentVariables: ExpressionVariables | undefined,
  varKey: string,
  propertyName: string,
): unknown => {
  if (!parentVariables) return undefined;
  const direct = parentVariables[varKey as keyof ExpressionVariables];
  if (direct !== undefined) return direct;
  if (propertyName === ElementPropertyName.Lifetime) {
    return parentVariables[
      EXPRESSION_VARIABLE_NAME.BUILDING_LIFETIME as keyof ExpressionVariables
    ];
  }
  return undefined;
};

/**
 * Apply {@link PropertyChange} fallback updates via the same partial builders as the
 * compute pipeline (quantity rows vs element.properties).
 */
const applyInheritedFallbackCountChanges = (
  element: IElement,
  changes: PropertyChange[],
): IElement => {
  let partial: Partial<OneOfElements> = {};
  let hasChanges = false;

  for (const change of changes) {
    const propertyName = change.name;
    const { fallbackCount } = change;
    if (!propertyName || fallbackCount === undefined) continue;

    // Quantities (element.quantity) — try before properties; names can overlap categories
    if (isExpressionValue(fallbackCount)) {
      const quantityPartial = elementQuantityFallbackCountToChange(element, {
        type: ProjectChangeType.ElementQuantityFallbackCount,
        elementId: element.id,
        quantityName: propertyName as ElementQuantityName,
        fallbackCount,
      });
      if (quantityPartial) {
        partial = mergeElementPartials(partial, quantityPartial);
        hasChanges = true;
        continue;
      }
    }

    const propertyPartial = elementPropertyFallbackCountToChange(element, {
      type: ProjectChangeType.ElementPropertyFallbackCount,
      elementId: element.id,
      propertyName,
      fallbackCount: fallbackCount as IElementProperty['fallbackCount'],
    });
    if (propertyPartial) {
      partial = mergeElementPartials(partial, propertyPartial);
      hasChanges = true;
    }
  }

  if (!hasChanges) return element;
  return applyChanges(element, partial as DeepPartial<IElement>);
};

/**
 * Update an inherited fallback count for properties that should inherit fallback count.
 * Needed for categories that need to check sbef when updating children/properties
 * @param element
 * @param variables
 * @returns
 */
export const updateInheritedPropertyFallbacks = <T extends OneOfElements>(
  element: T,
  variables: ExpressionVariables,
  parentElement?: IElement,
): T => {
  if (isElement(element)) {
    // Inherit from parent scope only — not this element's own resolved row values
    // (getParentValue on the child can already be 0 before the parent row is synced).
    const parentVariables = guards.is.object(variables.parent)
      ? (variables.parent as ExpressionVariables)
      : undefined;

    const changes: PropertyChange[] = getElementAndQuantityProperties(element)
      .filter((p) => {
        if (!parentVariables) return false;

        const quantityName = p.name as ElementQuantityName;
        const childQuantityRow = getElementQuantityByName(
          element,
          quantityName,
        );
        // DEV-3354: quantity inherit — parent element must still expose that quantity row
        if (childQuantityRow) {
          if (!parentElement) return false;
          if (!getElementQuantityByName(parentElement, quantityName)) {
            return false;
          }
        }

        const varKey = formatExpressionVariableName(p.name);
        const parentValue = resolveParentVariableForInheritFallback(
          parentVariables,
          varKey,
          p.name,
        );
        // Parent scope uses numeric resolved; select/switch use isInheritableFallbackCount
        if (guards.is.number(parentValue)) return true;
        return isInheritableFallbackCount(p, parentValue);
      })
      .map(({ name, type, fallbackCount }) => {
        const varKey = formatExpressionVariableName(name);
        const value = resolveParentVariableForInheritFallback(
          parentVariables,
          varKey,
          name,
        );
        // Keep expression; refresh resolved preview from parent.variables.parent
        const newFallback = isExpressionValue(fallbackCount)
          ? createExpression({
              ...fallbackCount,
              resolved: toNumber(value),
            })
          : value;

        return {
          name,
          type,
          fallbackCount: newFallback,
        } as PropertyChange;
      });

    if (!changes.length) return element;

    // Sync parent-scope resolved into inherit fallbackCount (quantity + properties)
    return applyInheritedFallbackCountChanges(element, changes) as T;
  }
  return element;
};
